/* eslint-disable max-statements */

import { Center, Loader, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCopy, IconEye, IconPencil } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Declarant, TemplatesCriteria } from '@e-origin/shared';

import { getInitialAdminDeclarantsCriteria, useAdminDeclarants, useAdminDeclarantsCriteriaUrlSync } from '@admin/hooks';
import { CheckedColumn, SearchBox, Table, TableActionEnum, TableActions } from '@components';
import { TABLE_MENU_THRESHOLD } from '@constants';
import { ContentLayout } from '@features/layout';
import { useSettings } from '@hooks/useSettings';
import { DuplicateDeclarant } from './DuplicateDeclarant';
import { EditDeclarantLogo } from './EditDeclarantLogo';

export const ManageDeclarants = () => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { data: settings } = useSettings();
  const [searchParams] = useSearchParams();
  const initialCriteria = getInitialAdminDeclarantsCriteria(searchParams);

  const [filters, setFilters] = useState<TemplatesCriteria['filters']>(initialCriteria.filters);
  const [sorting, setSorting] = useState<TemplatesCriteria['sorting']>(initialCriteria.sorting);
  const [pagination, setPagination] = useState<TemplatesCriteria['pagination']>(initialCriteria.pagination);
  useAdminDeclarantsCriteriaUrlSync({ filters, sorting, pagination });
  const { data: { data: { declarants = [], totalItems = 0 } = {} } = {}, isLoading: isDeclarantListLoading } = useAdminDeclarants({
    filters,
    sorting,
    pagination,
  });

  const searchDeclarant = (searchQuery: string) => {
    setFilters({
      ...filters,
      searchQuery: searchQuery,
    });
    setPagination({ ...pagination, page: 1 });
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination({ page: 1, size: pagination.size });
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const handleTableAction = async (actionType: string, row?: Declarant) => {
    switch (actionType) {
      case TableActionEnum.Duplicate: {
        modals.open({
          title: 'Duplicate declarant',
          size: 'lg',
          children: <DuplicateDeclarant declarant={row} />,
        });
        break;
      }
      case TableActionEnum.Edit: {
        navigate(`/${settings.adminPath}/manage-declarants/${row._id}`);
        break;
      }
      case TableActionEnum.Update: {
        modals.open({
          title: 'Update declarant logo',
          size: 'lg',
          children: <EditDeclarantLogo declarant={row} />,
        });
        break;
      }
      default:
        break;
    }
  };

  const columns = [
    {
      name: 'Declarant account name',
      selector: 'accountName',
      cell: (row) => row.accountName,
      sortable: true,
    },
    {
      name: 'Declarant name',
      selector: 'name',
      cell: (row) => row.name,
      sortable: true,
    },
    {
      name: 'Default',
      selector: 'default',
      cell: (row) => <CheckedColumn checked={row.default === true} />,
      sortable: true,
    },
    {
      name: 'Available',
      selector: 'deleted',
      cell: (row) => <CheckedColumn checked={row.deleted !== true} />,
      sortable: true,
    },
    {
      name: 'Actions',
      sticky: 'right' as any,
      cell: (row, rowIdx) => (
        <TableActions
          actions={[
            {
              iconComponent: <IconEye color={theme.colors[theme.primaryColor][6]} />,
              text: 'Edit',
              onClick: () => handleTableAction(TableActionEnum.Edit, row),
            },
            {
              iconComponent: <IconPencil color={theme.colors[theme.primaryColor][6]} />,
              text: 'Update logo',
              onClick: () => handleTableAction(TableActionEnum.Update, row),
            },
            {
              iconComponent: <IconCopy color={theme.colors[theme.primaryColor][6]} />,
              text: 'Duplicate',
              onClick: () => handleTableAction(TableActionEnum.Duplicate, row),
            },
          ]}
          disableAfterClick={false}
          moveMenuUpwards={pagination.size - rowIdx <= TABLE_MENU_THRESHOLD}
          data-pw={rowIdx}
        />
      ),
    },
  ];

  if (isDeclarantListLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Declarants</ContentLayout.Title>
        <ContentLayout.Filters>
          <SearchBox handleSearch={searchDeclarant} value={filters?.searchQuery} placeholder="Seach by account name..." data-pw="declarant-search-input" />
        </ContentLayout.Filters>
      </ContentLayout.Header>

      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={declarants || []}
          total={totalItems}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          isLoading={isDeclarantListLoading}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
